import FlexBox from "lib/components/common/flex-box";
import SimpleButton from "lib/components/common/simple-button";
import SimpleCard from "lib/components/common/simple-card";
import SimpleDataTable from "lib/components/common/simple-data-table";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleTabs from "lib/components/common/simple-tabs";
import SimpleTextbox from "lib/components/common/simple-textbox";
import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import {
  PATIENT_BOOKINGS_COLUMN,
  PRACTITIONER_ROOM_RESERVATIONS_COLUMNS,
} from "lib/constants/table-columns";
import { POSITIVE_NUMBER_VALIDATION_SCHEMA } from "lib/constants/validation-constants";
import useClinics from "lib/hooks/useClinics";
import useFormBinder from "lib/hooks/useFormBinder";
import usePatientBookings from "lib/hooks/usePatientBookings";
import usePractitioner from "lib/hooks/usePractitioner";
import useRoomReservations from "lib/hooks/useRoomReservations";
import useRooms from "lib/hooks/useRooms";
import useSubscriptions from "lib/hooks/useSubscription";
import {
  getArrayOfValuesFromArray,
  getItemFromArray,
  mergeArrays,
} from "lib/utils/array-utils";
import {
  callbackWithDelay,
  getFullName,
  isSuperAdminUser,
} from "lib/utils/util";
import AppointmentPage from "pages/practitioner/appointment-page";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  PiDatabaseBold,
  PiHouseLight,
  PiShoppingCartLight,
  PiUsersLight,
  PiXBold,
} from "react-icons/pi";
import { toast } from "sonner";
import RoomBookingTabPage from "./room-booking/page";
import usePractitionerSubscriptions from "lib/hooks/usePractitionerSubscriptions";
import RoomBookingSummaryTile from "lib/components/practitioners/room-booking-summary-tile";
import { AppContext } from "lib/contexts/app-context";
import { ButtonGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { PiPlusBold } from "react-icons/pi";

const breadcrumbItems = [
  {
    label: "Dashboard",
    path: PAGE_ROUTES.ADMIN.Dashboard,
  },
  {
    label: "Practitioners Management",
    path: PAGE_ROUTES.ADMIN.PractitionersManagement,
  },
] as BreadcrumbItem[];

const PRACTITIONER_MANAGEMENT_TABS: GeneralItem[] = [
  {
    id: 1,
    label: "Room Bookings",
    icon: <PiShoppingCartLight />,
  },
  {
    id: 2,
    label: "Room Allocations",
    icon: <PiHouseLight />,
  },
  {
    id: 3,
    label: "Patient Bookings",
    icon: <PiUsersLight />,
  },
];

const PractitionersManagementPage = () => {
  const navigateTo = useNavigate();
  const { appState } = useContext(AppContext);
  const [roomReservations, setRoomReservations] = useState<RoomReservation[]>(
    []
  );
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<
    string | null
  >(null);
  const [selectedTab, setSelectedTab] = useState<number>(3);
  const [practitionerSubscriptions, setPractitionerSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);
  const [clinicSubscriptions, setClinicSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);
  const [selectedSubscription, setSelectedSubscription] =
    useState<PractitionerSubscriptionDetails | null>(null);
  const [selectedRoomReservation, setSelectedRoomReservation] =
    useState<RoomReservation | null>(null);
  const [selectedBooking, setSelectedBooking] =
    useState<AppointmentBookingDetails | null>(null);

  /** States for room booking  */
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [clinicRooms, setClinicRooms] = useState<RoomDetails[]>([]);

  //const [roomBookingClinic, setRoomBookingClinic] = useState<string | null>();
  const [selectedClinicRoomId, setSelectedClinicRoomId] = useState<
    string | null
  >(null);

  // const [roomBookingDetails, setRoomBookingDetails] = useState<
  //   RoomBookingsDetails[]
  // >([]);
  /* States for room booking  */
  const [currentClinicCalendarDetails, setCurrentClinicCalendarDetails] =
    useState<CalendarDetails | null>(null);

  // const [unavailableDays, setUnavailableDays] = useState<UnavailableDay[]>(
  //   mergeArrays(
  //     currentClinicCalendarDetails?.[0]?.unavailableDates ?? [],
  //     currentClinicCalendarDetails?.[0]?.holidays ?? []
  //   )
  // );

  const { getClinics, getCalenderDetails } = useClinics();
  const { rooms, getRooms } = useRooms();

  const {
    loading: subscriptionsLoading,
    error: subscriptionsError,
    waiveOffSubscription,
    buySubscription,
  } = useSubscriptions();

  const {
    loading: practitionersLoading,
    error: practitionersError,
    practitioners,
    getAllPractitioners,
    getPractitionerSubscriptions,
    getPractitionerSubscriptionsWithPastDates,
    deleteSubscription,
    cancelSubscription,
    markSubscriptionAsPaid
  } = usePractitioner();

  const {
    byPractitioner,
    updateRoomReservation,
    deleteRoomReservation,
    error: roomReservationError,
    loading: roomReservationLoading,
  } = useRoomReservations();

  const {
    getSubscriptionsByClinic,
    error: practitionerSubscriptionsError,
    loading: practitionerSubscriptionsLoading,
  } = usePractitionerSubscriptions();

  const {
    getAppointments,
    appointments,
    loading: appointmentsLoading,
    error: appointmentsError,
  } = usePatientBookings();

  const { values, handleChange, setValues } = useFormBinder(
    selectedRoomReservation
  );

  const handlePractitionerSelection = (practitionerId?: string) => {
    setSelectedPractitionerId(practitionerId);

    if (!practitionerId) {
      return;
    }
    getPractitionerSubscriptionsWithPastDates(practitionerId, setPractitionerSubscriptions);
    byPractitioner(practitionerId, (data) => {
      if (Array.isArray(data)) {
        // Only update if data is an array (valid response)
        setRoomReservations(data);
      }
      // If data is not an array (error), do not update roomReservations
    });
    getAppointments(practitionerId);
  };

  const handleUpdateRoomReservation = async () => {
  if (!selectedRoomReservation) return;

  const result = await updateRoomReservation(values);

  // Check if the update failed
  if (!result || (result as any)?.error) {
    toast.error(
      (result as any)?.error || "Failed to update room reservation."
    );
    // Do not refresh or close the modal
    return;
  }

  // Success path
  toast.success("Room reservation updated successfully.");
  setSelectedRoomReservation(null);
  setValues(null);

  // Refresh only on success
  handlePractitionerSelection(selectedPractitionerId);
};

  const handleDeleteRoomReservation = () => {
    if (!selectedRoomReservation) {
      return;
    }

    deleteRoomReservation(values.id, () => {
      setSelectedRoomReservation(null);
      setValues(null);
      byPractitioner(selectedPractitionerId, setRoomReservations);
    });
  };

  useEffect(() => {
    getClinics(false, (clinics) => {
      setClinics(clinics);
      getCalenderDetails(clinics?.[0]?.id, setCurrentClinicCalendarDetails);
      if (clinics.length > 0) {
        //setRoomBookingClinic(clinics[0].id);
        getRooms(clinics[0].id, (roomsList) => {
          if (roomsList.length > 0) {
            getSubscriptionsByClinic(clinics[0].id, (data) => {
              setClinicSubscriptions(data);
              setClinicRooms(roomsList);
              setSelectedClinicRoomId(roomsList[0].id);
            });
          }
        });
      }
    });
    getAllPractitioners((practitionersList) => {
      handlePractitionerSelection(
        practitionersList.length > 0 ? practitionersList[0].id : null
      );
    });
  }, []);

  const practitionerNames = (practitioners ?? []).map(
    (practitioner) => `${practitioner.firstName} ${practitioner.lastName}`
  );
  const practitionerIds = getArrayOfValuesFromArray(practitioners, "id");

  // const handleSubscriptionSelection = (
  //   subscription: PractitionerSubscriptionDetails
  // ) => {
  //   if (!subscription?.isActive) {
  //     setSelectedSubscription(subscription);
  //   }
  // };

  const deletePractitionerSubscription = () => {
    if (!selectedSubscription) {
      toast.error("Please select a subscription to delete.");
      return;
    }
    deleteSubscription(selectedSubscription.id, (deletedSubscription) => {
      toast.success("Subscription deleted successfully.");
      setSelectedSubscription(null);
      getSubscriptionsByClinic(clinics[0].id, (data) => {
        setClinicSubscriptions(data);
        getPractitionerSubscriptionsWithPastDates(
          selectedPractitionerId,
          setPractitionerSubscriptions
        );
        byPractitioner(selectedPractitionerId, setRoomReservations);
      });
    });
  };

  const cancelPractitionerSubscription = () => {
    if (!selectedSubscription) {
      toast.error("Please select a subscription to cancel.");
      return;
    }
    cancelSubscription(selectedSubscription.id, (cancelledSubscription) => {
      toast.success("Subscription cancelled successfully.");
      setSelectedSubscription(null);

      getSubscriptionsByClinic(clinics[0].id, (data) => {
        setClinicSubscriptions(data);
        getPractitionerSubscriptionsWithPastDates(
          selectedPractitionerId,
          setPractitionerSubscriptions
        );
        byPractitioner(selectedPractitionerId, setRoomReservations);
      });
    });
  };

  const handleWaiveOffSubscription = () => {
    if (!selectedSubscription) {
      toast.error("Please select a subscription to waive off.");
      return;
    }
    waiveOffSubscription(selectedSubscription?.id, () => {
      toast.success(
        "Subscription waived off successfully. Ask practitioner to complete the payment."
      );
    });
    setSelectedSubscription(null);
  };

  const handlePractitionerRoomBooking = (
    roomBooking: PractitionerSubscriptionDetails,
    onSuccess: VoidFunction
  ) => {
    buySubscription(
      {
        clinicId: roomBooking.clinicId,
        dates: roomBooking.dates,
        id: roomBooking.id,
        isActive: roomBooking.isActive,
        months: roomBooking.months,
        perDayPrice: roomBooking.perDayPrice,
        practitionerId: roomBooking.practitionerId,
        practitionerEmail: selectedPractitionerDetails?.email,
        room: roomBooking.room,
        roomId: roomBooking.roomId,
        subscriptionId: roomBooking.subscriptionId,
        totalCost: roomBooking.totalCost,
        weekDays: [],
        fromDate: roomBooking.fromDate,
        toDate: roomBooking.toDate,
      },
      () => {
        getSubscriptionsByClinic(clinics[0].id, (data) => {
          setClinicSubscriptions(data);
          onSuccess();
        });
        getPractitionerSubscriptionsWithPastDates(
          selectedPractitionerId,
          setPractitionerSubscriptions
        );
      }
    );

    setSelectedClinicRoomId(clinicRooms[0].id);
  };

  const handleClinicChange = (clinicId: string) => {
    getRooms(clinicId, (roomsList) => {
      setClinicRooms(roomsList);
      setSelectedClinicRoomId(roomsList?.[0]?.id);
    });
    getSubscriptionsByClinic(clinicId, setClinicSubscriptions);
    getCalenderDetails(clinicId, setCurrentClinicCalendarDetails);
  };

  const handleCreateRoomReservations = () => {
    markSubscriptionAsPaid(selectedSubscription.practitionerId, selectedSubscription.id)
  }

  /** Calculated Vars */

  const selectedPractitionerDetails = getItemFromArray(
    practitioners ?? [],
    "id",
    selectedPractitionerId
  );

  const isSuperUser = isSuperAdminUser(appState?.userRole);

  const unavailableDays = mergeArrays<UnavailableDay>(
    currentClinicCalendarDetails?.[0]?.unavailableDates ?? [],
    currentClinicCalendarDetails?.[0]?.holidays ?? []
  );

  const handleNewAppointmentClick = () => {
    navigateTo(PAGE_ROUTES.ADMIN.BookAppointment);
  };

  /** Calculated Vars */

  return (
    <DashboardLayout breadcrumbItems={breadcrumbItems}>
      <SimpleCard
        title="Practitioners Management"
        isLoading={practitionersLoading}
        error={practitionersError}
        action={
          <SimpleDropdown
            label="Practitioners"
            options={practitionerNames}
            values={practitionerIds}
            selectedValue={selectedPractitionerId ?? ""}
            onChange={(e) => {
              handlePractitionerSelection(e.target.value);
            }}
          />
        }
      >
        <SimpleTabs
          tabs={PRACTITIONER_MANAGEMENT_TABS}
          selectedTabId={selectedTab}
          onTabChange={setSelectedTab}
        />
        {selectedTab === 1 && (
          <RoomBookingTabPage
            clinicRooms={clinicRooms}
            clinics={clinics}
            error={subscriptionsError || practitionerSubscriptionsError}
            isLoading={subscriptionsLoading || practitionerSubscriptionsLoading}
            practitionerId={selectedPractitionerId}
            practitionerName={getFullName(
              selectedPractitionerDetails?.firstName,
              selectedPractitionerDetails?.lastName
            )}
            onClinicChange={handleClinicChange}
            onRoomChange={setSelectedClinicRoomId}
            //roomBookingDetails={roomBookingDetails}
            selectedClinicRoomId={selectedClinicRoomId}
            practitionerSubscriptions={practitionerSubscriptions}
            unavailableDays={unavailableDays}
            clinicSubscriptions={clinicSubscriptions}
            onNewBooking={handlePractitionerRoomBooking}
            onSubscriptionClick={setSelectedSubscription}
          />
        )}
        {selectedTab === 2 && (
          <SimpleCard
            id="practitioner-rooms"
            isLoading={roomReservationLoading}
            error={roomReservationError}
          >
            <SimpleDataTable
              title="Practitioner Rooms"
              columns={PRACTITIONER_ROOM_RESERVATIONS_COLUMNS}
              data={roomReservations}
              isLoading={roomReservationLoading}
              error={roomReservationError}
              className="mt-3"
              onRowClick={(row) => {
                setSelectedRoomReservation(row as RoomReservation);
                setValues(row as RoomReservation);
              }}
            />
          </SimpleCard>
        )}
        {selectedTab === 3 && (
          <SimpleCard
            id="practitioner-bookings"
            isLoading={appointmentsLoading}
            error={appointmentsError}
          >
            <SimpleDataTable
              title="Practitioner Bookings"
              columns={PATIENT_BOOKINGS_COLUMN}
              data={appointments}
              isLoading={appointmentsLoading}
              error={appointmentsError}
              className="mt-3"
              onRowClick={(row) => {
                setSelectedBooking(row as AppointmentBookingDetails);
              }}
            />
          </SimpleCard>
        )}
      </SimpleCard>
      <SimpleModal
        id="edit-room-reservation-modal"
        open={!!selectedRoomReservation}
        close={() => setSelectedRoomReservation(null)}
        title="Edit details"
        size="sm"
        footer={
          <FlexBox
            justifyContent={isSuperUser ? "between" : "end"}
            className=""
          >
            {isSuperUser && (
              <SimpleButton
                variant="outline-danger"
                onClick={handleDeleteRoomReservation}
              >
                Delete
              </SimpleButton>
            )}
            <SimpleButton onClick={handleUpdateRoomReservation}>
              Update
            </SimpleButton>
          </FlexBox>
        }
      >
        <SimpleTextbox
          name="date"
          label="Date"
          value={values?.date}
          readOnly
          disabled
        />
        <SimpleTextbox
          name="clinicName"
          label="Clinic Name"
          value={values?.clinicName}
          readOnly
          disabled
        />
        <SimpleTextbox
          prefix="Room"
          name="room"
          //label="Room Number"
          type="number"
          value={values?.room}
          onChange={handleChange}
          required
          validationSchema={POSITIVE_NUMBER_VALIDATION_SCHEMA}
        />
      </SimpleModal>
      <SimpleModal
        id="booking-details-modal"
        open={!!selectedBooking}
        close={() => setSelectedBooking(null)}
        title="Booking Details"
        footer={
          <FlexBox justifyContent="end" className="">
            <SimpleButton onClick={() => setSelectedBooking(null)}>
              Close
            </SimpleButton>
          </FlexBox>
        }
      >
        <AppointmentPage
          practitionerId={selectedPractitionerId}
          definedDate={new Date(selectedBooking?.fromTime)}
        />
      </SimpleModal>
      <SimpleModal
        id="subscription-details-modal"
        open={!!selectedSubscription}
        close={() => setSelectedSubscription(null)}
        title="Booking Details"
        size={"sm"}
        footer={
          <FlexBox justifyContent="between" className="">
            {isSuperUser && (
              <ButtonGroup>
                <SimpleButton
                  variant="outline-danger"
                  onClick={deletePractitionerSubscription}
                  disabled={selectedSubscription?.isActive}
                  isLoading={practitionersLoading}
                >
                  <PiXBold />
                </SimpleButton>
                <SimpleButton
                  variant="outline-success"
                  onClick={handleCreateRoomReservations}
                  isLoading={practitionersLoading}
                >
                  <PiDatabaseBold />
                </SimpleButton>
              </ButtonGroup>
            )}
            <SimpleButton
              variant="outline-danger"
              onClick={cancelPractitionerSubscription}
              disabled={selectedSubscription?.isCancelled}
            >
              Cancel Subscription
            </SimpleButton>
            <SimpleButton
              onClick={handleWaiveOffSubscription}
              disabled={selectedSubscription?.isActive}
              isLoading={subscriptionsLoading}
            >
              Waive Off
            </SimpleButton>
          </FlexBox>
        }
      >
        {/* <SubscriptionSummaryTile subscription={selectedSubscription} /> */}
        <RoomBookingSummaryTile
          isEditable={isSuperUser}
          practitionerName={getFullName(
            selectedPractitionerDetails?.firstName,
            selectedPractitionerDetails?.lastName
          )}
          clinicName={selectedSubscription?.clinicName}
          roomNumber={selectedSubscription?.room}
          dates={(selectedSubscription?.dates ?? []).map(
            (date) => new Date(date)
          )}
          pricePerDay={selectedSubscription?.perDayPrice} // Assuming a fixed price per day for simplicity
          isShowFromDateToDate={true}
          rooms={rooms}
        />
      </SimpleModal>
    </DashboardLayout>
  );
};

export default PractitionersManagementPage;
